// T5b, free_allowance_2026_10 (owner, 2026-10-07: "put those countries on the same 2 free generations limit as
// everyone else and see which way we make more money from"). A new account in one of the one-free-generation
// countries (CONSTRAINED_GENERATIONS_COUNTRY_CODES: Brazil, the Philippines, India, Belgium, France) gets
// everyone's two free generations a month on one side, and keeps one on the other.
//
// The allowance is decided in the browser and nowhere else: the account starts with the default two
// (createUserOnFirebase), AuthContext lowers it to one once it learns the account's country (only while the
// account has made no set), and the monthly reset (checkAndUpdateUserGenerationLimits) and the plan's limits
// (getUserSubscriptionPlanAndLimits) read the country through getMonthlyGenerationLimit. The Python core and
// the Worker only spend the counter and refuse a Free generation at 0 (402 monthly_generation_limit_reached);
// neither knows the countries. So the test is decided where the country is learnt, and the side is written on
// the account (`experiment_variants.free_allowance_2026_10`), which every device and every later monthly
// reset reads.

import {
  CONSTRAINED_GENERATIONS_COUNTRY_CODES,
  getMonthlyGenerationLimit,
  NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED,
  UserData,
} from "../constants/GlobalTypesAndEnumsAndConstants";
import { FREE_ALLOWANCE_TEST, getFirstExperienceTestVariantForcedForQa } from "./firstExperienceTests";

type FreeAllowanceVariant = (typeof FREE_ALLOWANCE_TEST.variants)[number];

export function isInOneFreeGenerationCountry(countryCode: string | null | undefined): boolean {
  return Boolean(countryCode) && CONSTRAINED_GENERATIONS_COUNTRY_CODES.includes(countryCode.toUpperCase());
}

/**
 * The account's side: the one written on it when its country was learnt. Null for every other account, which
 * keeps today's allowance. With `includeSideForcedForQa`, a side forced by `?first_experience_preview` (development
 * builds) wins: for what a page shows only, never for what is written to the account's counter.
 */
export function getFreeAllowanceVariantForAccount(
  user: Pick<UserData, "country_code" | "experiment_variants"> | null | undefined,
  { includeSideForcedForQa = false }: { includeSideForcedForQa?: boolean } = {}
): FreeAllowanceVariant | null {
  if (!isInOneFreeGenerationCountry(user?.country_code)) return null;
  const variantForcedForQa = includeSideForcedForQa
    ? getFirstExperienceTestVariantForcedForQa(FREE_ALLOWANCE_TEST)
    : null;
  if (variantForcedForQa) return variantForcedForQa;
  const sideStoredOnAccount = user?.experiment_variants?.[FREE_ALLOWANCE_TEST.experimentId];
  return (FREE_ALLOWANCE_TEST.variants as readonly string[]).includes(sideStoredOnAccount)
    ? (sideStoredOnAccount as FreeAllowanceVariant)
    : null;
}

/**
 * The account's free generations a month: its country's, or everyone's two on T5b's "two_generations" side.
 * The monthly reset writes this number; only the plan limits (what pages show) include a side forced for QA.
 */
export function getMonthlyGenerationLimitForAccount(
  user: Pick<UserData, "country_code" | "experiment_variants"> | null | undefined,
  options: { includeSideForcedForQa?: boolean } = {}
): number {
  if (getFreeAllowanceVariantForAccount(user, options) === "two_generations")
    return NUM_OF_FREE_MONTHLY_GENERATIONS_ALLOWED;
  return getMonthlyGenerationLimit(user?.country_code);
}
